import React, { useMemo, useState } from 'react';
import { Issue } from '../../data/mockData';
import { Bug, BookOpen, Zap, Layers, ExternalLink, Check, AlertTriangle } from 'lucide-react';
import { useBoardColumns } from '../../hooks/useBoardColumns';
import { findLinkedGitData } from '../../services/issueGitLinker';
import { GitLinkedBadges } from '../shared/GitLinkedBadges';
import { BoardFilters, BoardFilterState, defaultFilters } from '../shared/BoardFilters';
import { BoardColumn } from '../../hooks/useBoardColumns';
import { IssueDetailPanel } from './IssueDetailPanel';
interface SprintBoardViewProps {
  issues: Issue[];
  jiraBaseUrl: string;
  githubPrs?: any[];
  githubCommits?: any[];
  githubBranches?: any[];
  /** Optional board ID to fetch real column configuration from Jira */
  boardId?: number | null;
}
type KnownStatus = 'Backlog' | 'To Do' | 'In Progress' | 'Review' | 'Done';
const COLUMN_STYLES: Record<string, {
  color: string;
  bg: string;
  count_color: string;
}> = {
  Backlog: {
    color: 'text-violet-400',
    bg: 'bg-violet-950/20',
    count_color: 'bg-violet-900/50 text-violet-400'
  },
  'To Do': {
    color: 'text-slate-400',
    bg: 'bg-slate-800/30',
    count_color: 'bg-slate-700 text-slate-400'
  },
  'In Progress': {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  Review: {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  Done: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  }
};
const DEFAULT_COLUMN_STYLE = {
  color: 'text-slate-400',
  bg: 'bg-slate-800/30',
  count_color: 'bg-slate-700 text-slate-400'
};
const STATUS_ORDER: string[] = ['Backlog', 'To Do', 'In Progress', 'Review', 'Done'];
function getTypeIcon(type: string) {
  switch (type) {
    case 'Bug':
      return <Bug size={11} className="text-red-400" data-id="element-6843" />;
    case 'Story':
      return <BookOpen size={11} className="text-emerald-400" data-id="element-6844" />;
    case 'Task':
      return <Zap size={11} className="text-blue-400" data-id="element-6845" />;
    case 'Epic':
      return <Layers size={11} className="text-violet-400" data-id="element-6846" />;
    default:
      return null;
  }
}
function getPriorityDot(priority: string) {
  switch (priority) {
    case 'High':
      return 'bg-red-400';
    case 'Medium':
      return 'bg-amber-400';
    case 'Low':
      return 'bg-blue-400';
    default:
      return 'bg-slate-500';
  }
}
// Known column name patterns → specific colors (same as KanbanBoard)
const KNOWN_COLUMN_STYLES: Record<string, {
  color: string;
  bg: string;
  count_color: string;
}> = {
  backlog: {
    color: 'text-violet-400',
    bg: 'bg-violet-950/20',
    count_color: 'bg-violet-900/50 text-violet-400'
  },
  'to do': {
    color: 'text-slate-400',
    bg: 'bg-slate-800/30',
    count_color: 'bg-slate-700 text-slate-400'
  },
  'por hacer': {
    color: 'text-slate-400',
    bg: 'bg-slate-800/30',
    count_color: 'bg-slate-700 text-slate-400'
  },
  'in progress': {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  'en progreso': {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  development: {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  review: {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  'in review': {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  'code review': {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  qa: {
    color: 'text-cyan-400',
    bg: 'bg-cyan-950/20',
    count_color: 'bg-cyan-900/50 text-cyan-400'
  },
  testing: {
    color: 'text-cyan-400',
    bg: 'bg-cyan-950/20',
    count_color: 'bg-cyan-900/50 text-cyan-400'
  },
  done: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  },
  completado: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  },
  closed: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  }
};
const DYNAMIC_COLUMN_COLORS = [{
  color: 'text-slate-400',
  bg: 'bg-slate-800/30',
  count_color: 'bg-slate-700 text-slate-400'
}, {
  color: 'text-blue-400',
  bg: 'bg-blue-950/20',
  count_color: 'bg-blue-900/50 text-blue-400'
}, {
  color: 'text-cyan-400',
  bg: 'bg-cyan-950/20',
  count_color: 'bg-cyan-900/50 text-cyan-400'
}, {
  color: 'text-amber-400',
  bg: 'bg-amber-950/20',
  count_color: 'bg-amber-900/50 text-amber-400'
}, {
  color: 'text-violet-400',
  bg: 'bg-violet-950/20',
  count_color: 'bg-violet-900/50 text-violet-400'
}, {
  color: 'text-emerald-400',
  bg: 'bg-emerald-950/20',
  count_color: 'bg-emerald-900/50 text-emerald-400'
}];
function getDynamicColumnStyle(name: string, index: number) {
  const known = KNOWN_COLUMN_STYLES[name.toLowerCase()];
  if (known) return known;
  return DYNAMIC_COLUMN_COLORS[index % DYNAMIC_COLUMN_COLORS.length];
}
interface SprintBoardCardProps {
  issue: Issue;
  jiraBaseUrl: string;
  linkedGitData?: import('../../services/issueGitLinker').LinkedGitData;
  onClick?: () => void;
}
function SprintBoardCard({
  issue,
  jiraBaseUrl,
  linkedGitData,
  onClick
}: SprintBoardCardProps) {
  return <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 transition-all group hover:border-slate-600 cursor-pointer" onClick={onClick} data-id="element-6847">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-6848">
        <div className="flex items-center gap-1.5" data-id="element-6849">
          {getTypeIcon(issue.type)}
          <span className="text-[10px] font-mono text-slate-500" data-id="element-6850">
            {issue.key}
          </span>
        </div>
        <a href={`${jiraBaseUrl}/${issue.key}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-indigo-400" data-id="element-6851">
          <ExternalLink size={11} data-id="element-6852" />
        </a>
      </div>

      {/* Summary */}
      <p className="text-sm text-slate-200 leading-snug mb-3 group-hover:text-indigo-300 transition-colors" data-id="element-6853">
        {issue.summary}
      </p>

      {/* Labels */}
      {issue.labels && issue.labels.length > 0 && <div className="flex flex-wrap gap-1 mb-3" data-id="element-6854">
          {issue.labels.slice(0, 2).map(label => <span key={label} className="px-1.5 py-0.5 bg-slate-800 text-slate-500 rounded text-[10px] border border-slate-700" data-id="element-6855">
              {label}
            </span>)}
        </div>}

      {/* Git linked badges */}
      {linkedGitData && (linkedGitData.prs.length > 0 || linkedGitData.commits.length > 0 || linkedGitData.branches.length > 0) && <div className="mb-2" data-id="element-6856">
            <GitLinkedBadges linked={linkedGitData} compact data-id="element-6857" />
          </div>}

      {/* Footer */}
      <div className="flex items-center justify-between" data-id="element-6858">
        <div className="flex items-center gap-1.5" data-id="element-6859">
          <div className={`w-1.5 h-1.5 rounded-full ${getPriorityDot(issue.priority)}`} title={issue.priority} data-id="element-6860" />

          {issue.points && <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700" data-id="element-6861">
              {issue.points}p
            </span>}
        </div>
        {issue.assignee && <div className="w-6 h-6 rounded-full bg-indigo-900/50 border border-indigo-800 flex items-center justify-center text-[9px] font-bold text-indigo-300" title={issue.assignee.name} data-id="element-6862">
            {issue.assignee.initials}
          </div>}
      </div>
    </div>;
}
export function SprintBoardView({
  issues,
  jiraBaseUrl,
  githubPrs,
  githubCommits,
  githubBranches,
  boardId
}: SprintBoardViewProps) {
  // Filter state
  const [filters, setFilters] = useState<BoardFilterState>(defaultFilters);
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);
  // Apply filters to issues
  const filteredIssues = useMemo(() => {
    return issues.filter(issue => {
      // Search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!issue.summary.toLowerCase().includes(q) && !issue.key.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Assignee
      if (filters.assignees.length > 0) {
        if (!issue.assignee || !filters.assignees.includes(issue.assignee.name)) {
          return false;
        }
      }
      // Priority
      if (filters.priorities.length > 0) {
        if (!filters.priorities.includes(issue.priority)) {
          return false;
        }
      }
      // Type
      if (filters.types.length > 0) {
        if (!filters.types.includes(issue.type)) {
          return false;
        }
      }
      return true;
    });
  }, [issues, filters]);
  // Fetch real board columns from Jira (only if boardId provided)
  const {
    columns: jiraColumns,
    isFromJira
  } = useBoardColumns(boardId ?? null);
  const columns = useMemo(() => {
    // If we have Jira columns, use them
    if (jiraColumns && jiraColumns.length > 0) {
      return jiraColumns.map((col, idx) => {
        const statusSet = new Set(col.statusNames.map(s => s.toLowerCase()));
        const colIssues = filteredIssues.filter(i => {
          // 1. Match by rawStatus (original Jira status) — most accurate
          if (i.rawStatus) {
            const raw = i.rawStatus.toLowerCase();
            if (statusSet.has(raw) || col.name.toLowerCase() === raw) return true;
          }
          // 2. Match by simplified status
          const st = i.status.toLowerCase();
          if (statusSet.has(st) || col.name.toLowerCase() === st) return true;
          // 3. Keyword matching: check if column's real statuses map to this simplified status
          for (const sn of col.statusNames) {
            const snl = sn.toLowerCase();
            if ((snl.includes('done') || snl.includes('closed') || snl.includes('resolved')) && st === 'done') return true;
            if (snl.includes('progress') && st === 'in progress') return true;
            if ((snl.includes('review') || snl.includes('testing') || snl.includes('qa')) && st === 'review') return true;
            if (snl.includes('backlog') && st === 'backlog') return true;
          }
          return false;
        });
        const totalPoints = colIssues.reduce((sum, i) => sum + (i.points || 0), 0);
        const style = getDynamicColumnStyle(col.name, idx);
        return {
          id: col.name,
          label: col.name,
          issues: colIssues,
          totalPoints,
          ...style
        };
      });
    }
    // Fallback: infer columns from issue statuses
    const uniqueStatuses = Array.from(new Set(filteredIssues.map(i => i.status)));
    const orderedStatuses = uniqueStatuses.sort((a, b) => {
      const indexA = STATUS_ORDER.indexOf(a);
      const indexB = STATUS_ORDER.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    return orderedStatuses.map(status => {
      const colIssues = filteredIssues.filter(i => i.status === status);
      const style = COLUMN_STYLES[status] || DEFAULT_COLUMN_STYLE;
      const totalPoints = colIssues.reduce((sum, i) => sum + (i.points || 0), 0);
      return {
        id: status,
        label: status,
        issues: colIssues,
        totalPoints,
        ...style
      };
    });
  }, [filteredIssues, jiraColumns]);
  return <div className="flex flex-col h-full" data-id="element-6863">
      <BoardFilters issues={issues} filters={filters} onFiltersChange={setFilters} filteredCount={filteredIssues.length} data-id="element-6864" />

      <div className="overflow-x-auto pb-4 pt-4 px-4" data-id="element-6865">
        {/* Column source indicator */}
        {isFromJira && <div className="mb-3 flex items-center gap-2" data-id="element-6866">
            <span className="text-[10px] text-indigo-400 bg-indigo-950/30 border border-indigo-900/40 px-2 py-0.5 rounded-full" data-id="element-6867">
              {columns.length} columnas · Configuración de Jira
            </span>
          </div>}
        <div className="flex gap-4 min-w-max" data-id="element-6868">
          {columns.map(col => <div key={col.id} className="flex flex-col w-72 shrink-0" data-id="element-6869">
              {/* Column Header */}
              <div className="flex items-center justify-between mb-3 px-1" data-id="element-6870">
                <div className="flex items-center gap-2" data-id="element-6871">
                  <span className={`text-sm font-semibold ${col.color}`} data-id="element-6872">
                    {col.label}
                  </span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${col.count_color}`} data-id="element-6873">
                    {col.issues.length}
                  </span>
                </div>
                <span className="text-[10px] text-slate-600" data-id="element-6874">
                  {col.totalPoints}p
                </span>
              </div>

              {/* Column Body */}
              <div className={`flex-1 rounded-xl p-2 space-y-2 min-h-[150px] ${col.bg}`} data-id="element-6875">
                {col.issues.map(issue => {
              const linked = findLinkedGitData(issue.key, githubPrs, githubCommits, githubBranches);
              return <SprintBoardCard key={issue.key} issue={issue} jiraBaseUrl={jiraBaseUrl} linkedGitData={linked} onClick={() => setSelectedIssueKey(issue.key)} data-id="element-6876" />;
            })}

                {/* Empty state */}
                {col.issues.length === 0 && <div className="h-20 rounded-lg border border-dashed border-slate-800 flex items-center justify-center text-xs text-slate-600" data-id="element-6877">
                    Sin issues
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      <IssueDetailPanel issueKey={selectedIssueKey} onClose={() => setSelectedIssueKey(null)} jiraBaseUrl={jiraBaseUrl} data-id="element-6878" />
    </div>;
}